// MCHD Room Scheduler - Room Form Component
// Form for creating and editing rooms
// Updated: 10-10-2025 19:49:52 - Added session template selection

import React, { useEffect, useState } from 'react';
import { Modal, TextInput, Textarea, NumberInput, Select, MultiSelect, Switch, Button, Group, Stack, Divider, Card, Text, Alert } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconClock, IconPlus, IconCheck } from '@tabler/icons-react';
import { useSessionTemplates } from '../../../hooks/api/useSessionTemplates';

interface Room {
  id: string;
  number: string;
  name: string;
  floorId: string;
  capacity: number;
  description?: string | null;
  specialtyTags: string[];
  allowedSpecialties: string[];
  defaultBuffer: number;
  status: 'ACTIVE' | 'OUT_OF_SERVICE';
  isActive: boolean;
  sessionTemplates?: Array<{ id: string; name: string; startTime: string; endTime: string }>;
  sessionTemplateIds?: string[];
}

interface RoomFormProps {
  room?: Room | null;
  floorId: string;
  onSubmit: (roomData: any) => void;
  onClose: () => void;
}

/**
 * Room Form Component
 * Handles creating and editing rooms
 */
export const RoomForm: React.FC<RoomFormProps> = ({
  room,
  floorId,
  onSubmit,
  onClose
}) => {
  const isEditing = !!room;

  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const { data: templatesData } = useSessionTemplates({ isActive: true });
  const templates = templatesData?.data || [];

  const form = useForm({
    initialValues: {
      number: '',
      name: '',
      capacity: 1,
      description: '',
      specialtyTags: [] as string[],
      allowedSpecialties: [] as string[],
      defaultBuffer: 0,
      status: 'ACTIVE' as 'ACTIVE' | 'OUT_OF_SERVICE',
      isActive: true,
      sessionTemplateIds: [] as string[] // NEW: Template assignment
    },
    validate: {
      number: (value) => {
        if (!value) return 'Room number is required';
        if (value.length < 1) return 'Room number must be at least 1 character';
        if (value.length > 20) return 'Room number cannot exceed 20 characters';
        return null;
      },
      name: (value) => {
        if (!value) return 'Room name is required';
        if (value.length < 2) return 'Room name must be at least 2 characters';
        if (value.length > 100) return 'Room name cannot exceed 100 characters';
        return null;
      },
      capacity: (value) => {
        if (!value) return 'Capacity is required';
        if (value < 1) return 'Capacity must be at least 1';
        if (value > 100) return 'Capacity cannot exceed 100';
        return null;
      },
      description: (value) => {
        if (value && value.length > 500) return 'Description cannot exceed 500 characters';
        return null;
      },
      defaultBuffer: (value) => {
        if (value < 0) return 'Default buffer cannot be negative';
        if (value > 120) return 'Default buffer cannot exceed 120 minutes';
        return null;
      }
    }
  });

  /**
   * Initialize form with room data when editing
   */
  useEffect(() => {
    if (room) {
      form.setValues({
        number: room.number,
        name: room.name,
        capacity: room.capacity,
        description: room.description || '',
        specialtyTags: room.specialtyTags,
        allowedSpecialties: room.allowedSpecialties,
        defaultBuffer: room.defaultBuffer,
        status: room.status,
        isActive: room.isActive,
        sessionTemplateIds: room.sessionTemplateIds || []
      });
    }
  }, [room]);

  /**
   * Handle form submission
   */
  const handleSubmit = (values: typeof form.values) => {
    const submitData = {
      ...values,
      floorId,
      description: values.description || null
    };
    
    onSubmit(submitData);
  };

  /**
   * Handle form reset
   */
  const handleReset = () => {
    form.reset();
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={isEditing ? 'Edit Room' : 'Create New Room'}
      size="lg"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Room Number and Name */}
          <Group grow>
            <TextInput
              label="Room Number"
              placeholder="e.g., A101, 201"
              required
              {...form.getInputProps('number')}
            />
            <TextInput
              label="Room Name"
              placeholder="e.g., Conference Room A, Operating Theater 1"
              required
              {...form.getInputProps('name')}
            />
          </Group>

          {/* Capacity and Buffer */}
          <Group grow>
            <NumberInput
              label="Capacity"
              placeholder="Maximum number of people"
              min={1}
              max={100}
              required
              {...form.getInputProps('capacity')}
            />
            <NumberInput
              label="Default Buffer (minutes)"
              placeholder="Buffer time between sessions"
              min={0}
              max={120}
              {...form.getInputProps('defaultBuffer')}
            />
          </Group>

          {/* Description */}
          <Textarea
            label="Description"
            placeholder="Room description (optional)"
            minRows={2}
            maxRows={4}
            {...form.getInputProps('description')}
          />

          {/* Specialty Tags */}
          <MultiSelect
            label="Specialty Tags"
            placeholder="Select or add specialty tags"
            data={[
              'Cardiology',
              'Neurology',
              'Orthopedics',
              'Pediatrics',
              'Surgery',
              'Emergency',
              'ICU',
              'Radiology',
              'Laboratory',
              'Pharmacy'
            ]}
            searchable
            creatable
            getCreateLabel={(query: string) => `+ Create "${query}"`}
            onCreate={(query: string) => query}
            maxSelectedValues={10}
            {...(form.getInputProps('specialtyTags') as any)}
          />

          {/* Allowed Specialties */}
          <MultiSelect
            label="Allowed Specialties"
            placeholder="Select allowed medical specialties"
            data={[
              'Cardiology',
              'Neurology',
              'Orthopedics',
              'Pediatrics',
              'Surgery',
              'Emergency Medicine',
              'Internal Medicine',
              'Radiology',
              'Pathology',
              'Anesthesiology'
            ]}
            searchable
            creatable
            getCreateLabel={(query: string) => `+ Create "${query}"`}
            onCreate={(query: string) => query}
            maxSelectedValues={20}
            {...(form.getInputProps('allowedSpecialties') as any)}
          />

          {/* NEW: Session Template Selection */}
          <Divider
            label={
              <Group gap="xs">
                <IconClock size={16} />
                <Text size="sm" fw={500}>Session Time Templates</Text>
              </Group>
            }
            labelPosition="left"
            my="md"
          />

          <MultiSelect
            label="Assign Session Templates"
            description="Select which session time templates this room will use"
            placeholder="Choose from existing templates"
            data={templates.map(t => ({
              value: t.id,
              label: `${t.name} (${t.startTime} - ${t.endTime})`
            }))}
            searchable
            {...form.getInputProps('sessionTemplateIds')}
            leftSection={<IconClock size={16} />}
          />

          {form.values.sessionTemplateIds.length > 0 && (
            <Alert icon={<IconCheck size={16} />} color="green" variant="light">
              <Text size="sm">
                {form.values.sessionTemplateIds.length} template(s) will be assigned to this room
              </Text>
            </Alert>
          )}

          {/* Status Fields (only for editing) */}
          {isEditing && (
            <>
              <Divider my="md" />
              <Select
                label="Room Status"
                placeholder="Select room status"
                data={[
                  { value: 'ACTIVE', label: 'Active - Available for use' },
                  { value: 'OUT_OF_SERVICE', label: 'Out of Service - Temporarily unavailable' }
                ]}
                {...form.getInputProps('status')}
              />
              <Switch
                label="Active Status"
                description="Whether this room is active and available for scheduling"
                {...form.getInputProps('isActive', { type: 'checkbox' })}
              />
            </>
          )}

          {/* Form Actions */}
          <Group justify="flex-end" mt="xl">
            <Button
              variant="outline"
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={form.values.name === ''} // Simple loading state
            >
              {isEditing ? 'Update Room' : 'Create Room'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
